import { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  WifiOff, MapPin, AlertTriangle, User, Camera, 
  Send, X, ChevronDown, Info, Shield, Building2
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { useToast } from '@/hooks/use-toast';
import { savePendingReport, generateOfflineId } from '@/utils/offlineStorage';
import { isNativePlatform, capturePhoto, photoToFile } from '@/utils/nativeCamera';
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from '@/components/ui/select';

interface OfflineHazardFormProps {
  profile: {
    id: string;
    site_id: string | null;
    company_id: string;
    display_name: string;
  };
  coworkers: Array<{ id: string; display_name: string; team: string }>;
  cachedLocation?: string;
  onClose: () => void;
  onSubmitSuccess: () => void;
}

// Hazard categories matching the database enum
const HAZARD_CATEGORIES = [
  // Unsafe Acts
  { value: 'no_ppe', label: 'Tidak pakai APD', classification: 'unsafe_act' },
  { value: 'unsafe_procedure', label: 'Prosedur tidak aman', classification: 'unsafe_act' },
  { value: 'improper_lifting', label: 'Cara angkat salah', classification: 'unsafe_act' },
  { value: 'bypassing_safety', label: 'Bypass sistem safety', classification: 'unsafe_act' },
  { value: 'not_following_sop', label: 'Tidak ikuti SOP', classification: 'unsafe_act' },
  { value: 'working_at_height_unsafe', label: 'Kerja ketinggian tidak aman', classification: 'unsafe_act' },
  // Unsafe Conditions
  { value: 'equipment_fault', label: 'Alat rusak/bermasalah', classification: 'unsafe_condition' },
  { value: 'spill', label: 'Tumpahan', classification: 'unsafe_condition' },
  { value: 'electrical_hazard', label: 'Bahaya listrik', classification: 'unsafe_condition' },
  { value: 'slippery_floor', label: 'Lantai licin', classification: 'unsafe_condition' },
  { value: 'poor_lighting', label: 'Pencahayaan buruk', classification: 'unsafe_condition' },
  { value: 'blocked_emergency_exit', label: 'Jalur evakuasi terhalang', classification: 'unsafe_condition' },
  { value: 'fire_hazard', label: 'Bahaya kebakaran', classification: 'unsafe_condition' },
  { value: 'chemical_exposure', label: 'Paparan kimia', classification: 'unsafe_condition' },
  { value: 'noise_hazard', label: 'Kebisingan tinggi', classification: 'unsafe_condition' },
  { value: 'sharp_objects', label: 'Benda tajam', classification: 'unsafe_condition' },
  { value: 'other', label: 'Lainnya', classification: 'unsafe_condition' },
];

const SEVERITY_LEVELS = [
  { value: 1, label: 'Sangat Ringan', description: 'Tidak ada cedera' },
  { value: 2, label: 'Ringan', description: 'P3K, cedera ringan' },
  { value: 3, label: 'Sedang', description: 'Rawat medis' },
  { value: 4, label: 'Berat', description: 'Cacat / rawat inap' },
  { value: 5, label: 'Fatal', description: 'Kematian' },
];

const PROBABILITY_LEVELS = [
  { value: 1, label: 'Sangat Jarang', description: 'Hampir tidak mungkin' },
  { value: 2, label: 'Jarang', description: 'Bisa terjadi sekali-kali' },
  { value: 3, label: 'Mungkin', description: 'Kemungkinan besar terjadi' },
  { value: 4, label: 'Sering', description: 'Sering terjadi' },
  { value: 5, label: 'Hampir Pasti', description: 'Pasti terjadi' },
];

// Calculate risk rating from score
function getRiskRating(score: number): string {
  if (score <= 4) return 'very_low';
  if (score <= 9) return 'low';
  if (score <= 14) return 'medium';
  if (score <= 19) return 'high';
  return 'very_high';
}

export function OfflineHazardForm({ 
  profile, 
  coworkers,
  cachedLocation,
  onClose, 
  onSubmitSuccess 
}: OfflineHazardFormProps) {
  const { toast } = useToast();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [photoPreview, setPhotoPreview] = useState<string | null>(null);
  const [photoBlob, setPhotoBlob] = useState<Blob | null>(null);
  const [showReportedPersonPicker, setShowReportedPersonPicker] = useState(false);
  const [gpsCoordinates, setGpsCoordinates] = useState<string>('');

  const [formData, setFormData] = useState({
    location: cachedLocation || '',
    hazardCategory: '',
    description: '',
    reportedPersonName: '', // Free text for name when offline
    reportedWorkerId: '', // If selecting from coworkers list
    severity: 2,
    probability: 2,
    workActivity: '',
    causeDetails: '',
  });

  // Capture GPS on mount
  useEffect(() => {
    if ('geolocation' in navigator) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setGpsCoordinates(`${position.coords.latitude},${position.coords.longitude}`);
        },
        (error) => console.log('GPS not available:', error)
      );
    }
  }, []);

  const handlePhotoCapture = async () => {
    if (isNativePlatform()) {
      try {
        const photo = await capturePhoto('prompt');
        if (!photo) return;
        
        const file = await photoToFile(photo);
        if (file) {
          setPhotoBlob(file);
          setPhotoPreview(photo.webPath || null);
        }
      } catch (error) {
        console.error('Camera error:', error);
        toast({ 
          title: 'Gagal mengambil foto', 
          description: 'Coba lagi atau skip foto',
          variant: 'destructive' 
        });
      }
    } else {
      fileInputRef.current?.click();
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    setPhotoBlob(file);
    const reader = new FileReader();
    reader.onloadend = () => setPhotoPreview(reader.result as string);
    reader.readAsDataURL(file);
  };

  const handleSubmit = async () => {
    // Validation
    if (!formData.description.trim()) {
      toast({ 
        title: 'Deskripsi diperlukan', 
        description: 'Jelaskan bahaya yang ditemukan',
        variant: 'destructive' 
      });
      return;
    }

    if (!formData.location.trim()) {
      toast({ 
        title: 'Lokasi diperlukan', 
        description: 'Isi lokasi dimana bahaya ditemukan',
        variant: 'destructive' 
      });
      return;
    }

    if (!formData.hazardCategory) {
      toast({ 
        title: 'Jenis bahaya diperlukan', 
        description: 'Pilih kategori bahaya',
        variant: 'destructive' 
      });
      return;
    }

    setIsSubmitting(true);

    try {
      const category = HAZARD_CATEGORIES.find(c => c.value === formData.hazardCategory);
      const riskScore = formData.severity * formData.probability;
      const riskRating = getRiskRating(riskScore);

      // Prepare report data matching peer_reports table schema
      const reportData = {
        // Required fields
        description: formData.description,
        location: formData.location,
        hazard_classification: category?.classification || 'unsafe_condition',
        hazard_category: formData.hazardCategory,
        severity: formData.severity,
        probability: formData.probability,
        risk_score: riskScore,
        risk_rating: riskRating,
        verification_status: 'pending_verification',
        
        // Reporter info
        reporter_id: profile.id,
        site_id: profile.site_id,
        
        // Reported person (important for worker diagnostics & network analysis)
        reported_worker_id: formData.reportedWorkerId || null,
        // Store name in description if ID not available
        
        // Additional context
        work_activity: formData.workActivity || null,
        cause_details: formData.causeDetails || null,
        gps_coordinates: gpsCoordinates || null,
        
        // Quality/scoring flags
        quality_score: 50, // Lower quality for offline submissions
        xp_pending: true,
        xp_awarded: 0,
        audio_input_method: 'offline_form',
        
        // Metadata
        is_false_report: false,
        risk_score_applied: false,
      };

      // If reported person name provided but no ID, append to description
      if (formData.reportedPersonName && !formData.reportedWorkerId) {
        reportData.description = `${formData.description}\n\n[Pelaporan offline - Orang yang dilaporkan: ${formData.reportedPersonName}]`;
      }

      // Generate unique offline ID
      const offlineId = generateOfflineId();

      // Save to IndexedDB
      await savePendingReport({
        id: offlineId,
        data: reportData,
        photoBlob: photoBlob || undefined,
        createdAt: new Date().toISOString(),
        retryCount: 0,
      });

      toast({
        title: 'Laporan Disimpan Offline',
        description: 'Akan dikirim otomatis saat koneksi kembali',
      });

      onSubmitSuccess();
    } catch (error) {
      console.error('Failed to save offline report:', error);
      toast({
        title: 'Gagal Menyimpan',
        description: 'Coba lagi',
        variant: 'destructive',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const selectedCategory = HAZARD_CATEGORIES.find(c => c.value === formData.hazardCategory);
  const riskScore = formData.severity * formData.probability;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 bg-background/95 backdrop-blur-sm overflow-hidden"
    >
      <div className="h-full flex flex-col overflow-hidden">
        {/* Header */}
        <div className="bg-amber-500 text-amber-950 px-4 py-3 shadow-lg z-10 pt-safe shrink-0">
          <div className="max-w-2xl mx-auto flex items-center justify-between">
            <div className="flex items-center gap-3">
              <WifiOff className="h-5 w-5" />
              <div>
                <h1 className="font-semibold">Form Laporan Offline</h1>
                <p className="text-xs opacity-80">Data akan disinkronkan saat online</p>
              </div>
            </div>
            <Button 
              variant="ghost" 
              size="icon"
              onClick={onClose}
              className="text-amber-950 hover:bg-amber-600/20"
            >
              <X className="h-5 w-5" />
            </Button>
          </div>
        </div>

        {/* Scrollable Content */}
        <div className="flex-1 overflow-y-auto min-h-0" style={{ WebkitOverflowScrolling: 'touch' }}>
          <div className="max-w-2xl mx-auto px-4 py-6 space-y-6 pb-8">
            {/* Info Banner */}
            <div className="flex items-start gap-3 p-4 bg-blue-50 dark:bg-blue-950/30 rounded-xl border border-blue-200 dark:border-blue-800">
              <Info className="h-5 w-5 text-blue-600 shrink-0 mt-0.5" />
              <div className="text-sm text-blue-800 dark:text-blue-200">
                <p className="font-medium mb-1">Mode Offline Aktif</p>
                <p className="text-xs opacity-80">
                  Asisten AI SORIN tidak tersedia offline. Isi form ini secara manual 
                  dan laporan akan diproses setelah koneksi kembali.
                </p>
              </div>
            </div>

            {/* Location */}
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <MapPin className="h-4 w-4 text-muted-foreground" />
                Lokasi Bahaya *
              </Label>
              <Input
                placeholder="Contoh: Workshop A, Gudang B, Area Welding"
                value={formData.location}
                onChange={(e) => setFormData(prev => ({ ...prev, location: e.target.value }))}
                className="h-12"
              />
              {gpsCoordinates && (
                <p className="text-xs text-muted-foreground">
                  📍 GPS: {gpsCoordinates}
                </p>
              )}
            </div>

            {/* Hazard Category */}
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <AlertTriangle className="h-4 w-4 text-muted-foreground" />
                Jenis Bahaya *
              </Label>
              <Select
                value={formData.hazardCategory}
                onValueChange={(value) => setFormData(prev => ({ ...prev, hazardCategory: value }))}
              >
                <SelectTrigger className="h-12">
                  <SelectValue placeholder="Pilih jenis bahaya" />
                </SelectTrigger>
                <SelectContent>
                  <div className="px-2 py-1.5 text-xs font-semibold text-muted-foreground">
                    Tindakan Tidak Aman
                  </div>
                  {HAZARD_CATEGORIES.filter(c => c.classification === 'unsafe_act').map((cat) => (
                    <SelectItem key={cat.value} value={cat.value}>
                      {cat.label}
                    </SelectItem>
                  ))}
                  <div className="px-2 py-1.5 text-xs font-semibold text-muted-foreground border-t mt-1 pt-2">
                    Kondisi Tidak Aman
                  </div>
                  {HAZARD_CATEGORIES.filter(c => c.classification === 'unsafe_condition').map((cat) => (
                    <SelectItem key={cat.value} value={cat.value}>
                      {cat.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {selectedCategory && (
                <p className="text-xs text-muted-foreground">
                  Klasifikasi: {selectedCategory.classification === 'unsafe_act' ? 'Tindakan tidak aman' : 'Kondisi tidak aman'}
                </p>
              )}
            </div>

            {/* Description */}
            <div className="space-y-2">
              <Label>Deskripsi Bahaya *</Label>
              <Textarea
                placeholder="Jelaskan bahaya yang ditemukan secara detail..."
                value={formData.description}
                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                className="min-h-[100px]"
              />
            </div>

            {/* Work Activity */}
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <Building2 className="h-4 w-4 text-muted-foreground" />
                Aktivitas Kerja Saat Itu
              </Label>
              <Input
                placeholder="Contoh: Pengelasan pipa, bongkar muat, dll"
                value={formData.workActivity}
                onChange={(e) => setFormData(prev => ({ ...prev, workActivity: e.target.value }))}
              />
            </div>

            {/* Reported Person (for unsafe acts) */}
            {selectedCategory?.classification === 'unsafe_act' && (
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <User className="h-4 w-4 text-muted-foreground" />
                  Orang yang Melakukan (Opsional)
                </Label>
                <p className="text-xs text-muted-foreground mb-2">
                  Info ini penting untuk analisis perilaku dan intervensi. Identitas dijaga rahasia.
                </p>
                
                {coworkers.length > 0 ? (
                  <Select
                    value={formData.reportedWorkerId}
                    onValueChange={(value) => setFormData(prev => ({ 
                      ...prev, 
                      reportedWorkerId: value,
                      reportedPersonName: '' 
                    }))}
                  >
                    <SelectTrigger className="h-12">
                      <SelectValue placeholder="Pilih dari daftar (jika ada)" />
                    </SelectTrigger>
                    <SelectContent>
                      {coworkers.map((worker) => (
                        <SelectItem key={worker.id} value={worker.id}>
                          {worker.display_name} - {worker.team}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                ) : (
                  <Input
                    placeholder="Nama atau ciri-ciri orang tersebut"
                    value={formData.reportedPersonName}
                    onChange={(e) => setFormData(prev => ({ ...prev, reportedPersonName: e.target.value }))}
                  />
                )}
                
                {!formData.reportedWorkerId && coworkers.length > 0 && (
                  <Input
                    placeholder="Atau ketik nama/ciri-ciri jika tidak ada di daftar"
                    value={formData.reportedPersonName}
                    onChange={(e) => setFormData(prev => ({ ...prev, reportedPersonName: e.target.value }))}
                    className="mt-2"
                  />
                )}
              </div>
            )}

            {/* Risk Assessment */}
            <div className="p-4 bg-muted/50 rounded-xl space-y-4">
              <h3 className="font-medium flex items-center gap-2">
                <Shield className="h-4 w-4" />
                Penilaian Risiko
              </h3>

              {/* Severity */}
              <div className="space-y-2">
                <Label className="text-sm">Tingkat Keparahan</Label>
                <div className="grid grid-cols-5 gap-1">
                  {SEVERITY_LEVELS.map((level) => (
                    <button
                      key={level.value}
                      onClick={() => setFormData(prev => ({ ...prev, severity: level.value }))}
                      className={`p-2 rounded-lg text-center transition-all ${
                        formData.severity === level.value
                          ? 'bg-primary text-primary-foreground shadow-md'
                          : 'bg-background hover:bg-muted border border-border/50'
                      }`}
                    >
                      <div className="text-lg font-bold">{level.value}</div>
                      <div className="text-[10px] leading-tight">{level.label}</div>
                    </button>
                  ))}
                </div>
              </div>

              {/* Probability */}
              <div className="space-y-2">
                <Label className="text-sm">Kemungkinan Terjadi</Label>
                <div className="grid grid-cols-5 gap-1">
                  {PROBABILITY_LEVELS.map((level) => (
                    <button
                      key={level.value}
                      onClick={() => setFormData(prev => ({ ...prev, probability: level.value }))}
                      className={`p-2 rounded-lg text-center transition-all ${
                        formData.probability === level.value
                          ? 'bg-primary text-primary-foreground shadow-md'
                          : 'bg-background hover:bg-muted border border-border/50'
                      }`}
                    >
                      <div className="text-lg font-bold">{level.value}</div>
                      <div className="text-[10px] leading-tight">{level.label}</div>
                    </button>
                  ))}
                </div>
              </div>

              {/* Risk Score Display */}
              <div className={`p-3 rounded-lg text-center ${
                riskScore <= 4 ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300' :
                riskScore <= 9 ? 'bg-lime-100 text-lime-800 dark:bg-lime-900/30 dark:text-lime-300' :
                riskScore <= 14 ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300' :
                riskScore <= 19 ? 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300' :
                'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300'
              }`}>
                <div className="text-2xl font-bold">{riskScore}</div>
                <div className="text-sm font-medium">
                  Skor Risiko: {getRiskRating(riskScore).replace('_', ' ').toUpperCase()}
                </div>
              </div>
            </div>

            {/* Photo */}
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <Camera className="h-4 w-4 text-muted-foreground" />
                Foto Bukti (Opsional)
              </Label>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                capture="environment"
                onChange={handleFileSelect}
                className="hidden"
              />
              
              {photoPreview ? (
                <div className="relative">
                  <img 
                    src={photoPreview} 
                    alt="Preview" 
                    className="w-full max-h-48 object-cover rounded-xl"
                  />
                  <Button
                    variant="destructive"
                    size="icon"
                    className="absolute top-2 right-2 h-8 w-8"
                    onClick={() => {
                      setPhotoPreview(null);
                      setPhotoBlob(null);
                    }}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              ) : (
                <Button
                  variant="outline"
                  className="w-full h-24 border-dashed"
                  onClick={handlePhotoCapture}
                >
                  <Camera className="h-6 w-6 mr-2" />
                  Ambil Foto
                </Button>
              )}
            </div>

            {/* Cause Details */}
            <div className="space-y-2">
              <Label>Detail Penyebab (Opsional)</Label>
              <Textarea
                placeholder="Apa penyebab bahaya ini terjadi? Mengapa bisa terjadi?"
                value={formData.causeDetails}
                onChange={(e) => setFormData(prev => ({ ...prev, causeDetails: e.target.value }))}
                className="min-h-[80px]"
              />
            </div>
          </div>
        </div>

        {/* Bottom Submit Bar (fixed layout slot, not floating) */}
        <div 
          className="shrink-0 px-4 pt-4 bg-background/95 backdrop-blur-xl border-t"
          style={{ paddingBottom: "calc(1rem + env(safe-area-inset-bottom, 0px))" }}
        >
          <div className="max-w-2xl mx-auto">
            <Button
              onClick={handleSubmit}
              disabled={isSubmitting || !formData.description || !formData.location || !formData.hazardCategory}
              className="w-full h-14 text-base font-semibold rounded-full"
            >
              {isSubmitting ? (
                'Menyimpan...'
              ) : (
                <>
                  <Send className="h-5 w-5 mr-2" />
                  Simpan Laporan Offline
                </>
              )}
            </Button>
          </div>
        </div>
      </div>
    </motion.div>
  );
}
